import { useMemo } from 'react';

import { Anchor, Box, Button, Icon, Text, TextButton } from '@zillow/constellation-10';
import { IconUserGroupFilled } from '@zillow/constellation-icons';

import { TourSchedulingUpsell, useTouringTreatmentForZuidAndListing } from '@zg-rentals/touring';

import { useGetInterestedRenters } from '../../../../hooks/queries/useGetInterestedRenters';
import browserUtils from '../../../../utils/browserUtils';
import stringHelper from '../../../../utils/stringHelper';

import { VERTICAL_DISPLAY_TYPE } from '../../../../constants/PropertiesConstants';

import { useAuth } from '../../../../components/AuthProvider';
import EmptyState from '../../../../components/EmptyState';
import ErrorMessagesAlert from '../../../../components/ErrorMessagesAlert';
import SimpleCard from '../../../../components/SimpleCard';

import { noUnderlineAnchorStyles } from '../../../../styles/moduleCards';

import {
  emitTourSchedulingUpsellSetTourAvailabilityCtaEvent,
  TOUR_SCHEDULING_UPSELL_SET_TOUR_AVAILABILITY_CTA_EVENT_ID,
} from '../../../../analytics/events/overviewPage';
import ConnectedModuleCard from '../../../ConnectedModuleCard';
import LeadDetails, { type Lead } from './LeadDetails';

const TITLE = 'Find a renter';
const MOCK_LEAD = {
  conversationId: '',
  latestContact: {
    isPhoneLead: false,
    isRead: true,
    messageDateMs: '',
    messageText: '',
    notRepliedTo: false,
  },
  renterInfo: { renterPhoneNumber: '', renterName: '' },
  status: { isConversationBlockedByZillow: false },
  statusLabel: { linkId: '', text: '' },
};

type Props = {
  listingAlias: string;
  street?: string;
  isFeed?: boolean;
  limit?: number;
  displayType?: VERTICAL_DISPLAY_TYPE;
  showScreeningCriteriaCTA?: boolean;
  isScreeningCriteriaSet?: boolean;
};

const InterestedRenters = ({
  listingAlias,
  street = '',
  isFeed = false,
  limit = 3,
  displayType = VERTICAL_DISPLAY_TYPE.SIMPLE,
  showScreeningCriteriaCTA = false,
  isScreeningCriteriaSet = false,
}: Props) => {
  const { user } = useAuth();

  const {
    data,
    isError: hasError,
    isPending: isGetInterestedRentersLoading,
  } = useGetInterestedRenters({
    listingAlias,
    clientTimeZone: browserUtils.timezone(),
    limit,
  });

  const { data: touringTreatment } = useTouringTreatmentForZuidAndListing(
    { listingAlias },
    { enabled: Boolean(listingAlias), errorDetails: 'InterestedRenters - Error fetching touring treatment' },
  );

  const inquiriesPath = `/rental-manager/lead-management/${listingAlias}`;

  const { leads, totalLeadCount = 0 } = data || {};
  const leadListLength = leads?.length || 0;
  const remainingTotalLeadCount = totalLeadCount - leadListLength;

  const handleTourSchedulingUpsellClick = () => {
    if (user) {
      emitTourSchedulingUpsellSetTourAvailabilityCtaEvent({
        currentProperty: { listingAlias },
        user,
      });
    }
  };

  const renderCTA = useMemo(() => {
    if (leadListLength === 0) return null;

    if (displayType === VERTICAL_DISPLAY_TYPE.FULL) {
      return (
        <Button asChild tone="neutral" emphasis="outlined" size="sm">
          <a href={inquiriesPath} target="_self">
            View all
          </a>
        </Button>
      );
    }

    return (
      <TextButton tone="brand" asChild>
        <a href={inquiriesPath} target="_self">
          Details
        </a>
      </TextButton>
    );
  }, [leadListLength, inquiriesPath, displayType]);

  const leadsToRender = isGetInterestedRentersLoading ? Array(2).fill(MOCK_LEAD) : data?.leads;

  const renderLeadDetailsList = useMemo(() => {
    if (!leadsToRender) return null;

    return leadsToRender.map((lead: Lead, i: number) => (
      <Box key={lead.conversationId || i}>
        <LeadDetails
          lead={lead}
          includeDivider={i !== (data?.leads?.length || 0) - 1}
          listingAlias={listingAlias}
          isLoading={isGetInterestedRentersLoading}
        />
      </Box>
    ));
  }, [leadsToRender, listingAlias, isGetInterestedRentersLoading, data?.leads]);

  const renderLeadListWithMoreLeadsCTA = () => {
    if (isGetInterestedRentersLoading) {
      return <SimpleCard isLoading={true} />;
    }

    return (
      <>
        {renderLeadDetailsList}
        {remainingTotalLeadCount > 0 && (
          <>
            <Box css={{ marginBottom: '600' }} />
            <div data-testid="MoreLeadsContainer">
              <Text
                textStyle="body-sm"
                asChild
                css={{ color: 'text.subtle', _hover: { color: 'text.action.hero.default' } }}
              >
                <Anchor css={noUnderlineAnchorStyles} href={inquiriesPath} target="_blank" rel="noopener noreferrer">
                  {`+ ${remainingTotalLeadCount} more ${stringHelper.pluralizeWord(
                    remainingTotalLeadCount,
                    'lead',
                    'leads',
                  )}`}
                </Anchor>
              </Text>
            </div>
          </>
        )}
      </>
    );
  };

  if (hasError) {
    return (
      <ConnectedModuleCard title={TITLE}>
        <ErrorMessagesAlert
          appearance="warning"
          errorMessages={[{ text: 'Unable to load inquiries. Please refresh the page to try again.' }]}
        />
      </ConnectedModuleCard>
    );
  }

  if (displayType === VERTICAL_DISPLAY_TYPE.FULL) {
    const screeningCriteriaPath = `/rental-manager/properties/${listingAlias}/screening-criteria`;
    const bodyPrefix = isScreeningCriteriaSet ? 'Adjusting your' : 'Setting screening';
    const screeningCriteriaBody = `${bodyPrefix} criteria could help attract more renters to your property.`;
    const screeningCriteriaLabel = isScreeningCriteriaSet
      ? 'Adjust screening preferences'
      : 'Set screening preferences';

    if (leadListLength === 0 && !isGetInterestedRentersLoading) {
      return (
        <div data-testid="InterestedRenters">
          <EmptyState
            data-testid="EmptyInterestedRentersCard"
            icon={<Icon render={<IconUserGroupFilled />} size="md" css={{ color: 'icon.subtle' }} />}
            heading="No leads yet"
            headingLevel={4}
            align="left"
            css={{ height: '100%' }}
            body={showScreeningCriteriaCTA ? screeningCriteriaBody : undefined}
            secondaryAction={
              showScreeningCriteriaCTA ? { label: screeningCriteriaLabel, href: screeningCriteriaPath } : undefined
            }
          />
        </div>
      );
    }

    return (
      <div data-testid="InterestedRenters">
        <ConnectedModuleCard
          data-testid="InterestedRentersFull"
          title={TITLE}
          ctaComponent={renderCTA}
          ctaFontColor="text.subtle"
          ctaFontType="body-sm"
          justifyHeadingContent="center"
        >
          {renderLeadListWithMoreLeadsCTA()}
        </ConnectedModuleCard>
      </div>
    );
  }

  return (
    <div data-testid="InterestedRenters">
      <ConnectedModuleCard
        data-testid="InterestedRentersSimple"
        title={TITLE}
        ctaComponent={renderCTA}
        ctaFontType="body-sm"
        justifyHeadingContent="center"
      >
        {touringTreatment &&
          !touringTreatment.isTouringEnabledForListing &&
          !touringTreatment.hasAvailability &&
          !isGetInterestedRentersLoading &&
          !isFeed && (
            <Box css={{ marginBottom: '400' }}>
              <TourSchedulingUpsell
                listingAlias={listingAlias}
                modalHeaderText={street}
                onClick={handleTourSchedulingUpsellClick}
                clickStreamMetadata={{ user: user ?? undefined, currentProperty: { listingAlias } }}
                eventId={TOUR_SCHEDULING_UPSELL_SET_TOUR_AVAILABILITY_CTA_EVENT_ID}
                canAccessPostingPathTourDuration={true}
              />
            </Box>
          )}

        {leadListLength > 0 || isGetInterestedRentersLoading ? (
          renderLeadListWithMoreLeadsCTA()
        ) : (
          <EmptyState
            data-testid="EmptyInterestedRentersCardSimple"
            icon={<Icon render={<IconUserGroupFilled />} size="md" css={{ color: 'icon.subtle' }} />}
            heading="No leads yet"
            headingLevel={3}
            body="Messages from interested renters will appear here."
          />
        )}
      </ConnectedModuleCard>
    </div>
  );
};

export default InterestedRenters;
